import React from 'react';
import _ from 'lodash';
import { Input } from './Input';
import { Message } from './Message';
import { Navigate } from './Navigate';
import { Button } from './Button';
import {__, formatDate, getDomain, isEmptyString, request, toNumber} from '../functions';
import '../assets/styles/chat-window.css';
import SendIcon from "@mui/icons-material/Send";
import PersonIcon from "@mui/icons-material/Person";
import FaceRetouchingNaturalIcon from '@mui/icons-material/FaceRetouchingNatural';
import {ButtonBase, IconButton, Tooltip} from "@mui/material";
import {MARKET} from "../config";

let purifier = null;

const preloadPurifier = async () => {
    if (!purifier) {
        const module = await import('dompurify');
        purifier = module.default;
    }
};

const purify = text => {
    if (!purifier) {
        return '';
    }

    return purifier.sanitize(text);
};

class ChatWindow extends Navigate {
    static defaultProps = {
        user: {},
    };

    state = {
        message: '',
        data: {},
        thread_id: 0,
        createLoading: false,
    };

    thread_interval = null;

    async componentDidMount() {
        await preloadPurifier();

        this.fetch();
    }

    componentWillUnmount() {
        this.clearThreadInterval();
    }

    clearThreadInterval() {
        if (this.thread_interval !== null) {
            clearInterval(this.thread_interval);
        }
    }

    fetch() {
        const { onClose } = this.props;

        this.clearThreadInterval();

        this.setState({ thread_id: 0, data: {} });

        request(`/agent-helper-threads/index`).then(response => {
            const { status, data } = response.data;

            if (status === 'error') {
                onClose();
                return;
            }

            this.setState({ data });
        });
    }

    showThread(id) {
        const { onClose } = this.props;

        this.clearThreadInterval();

        this.setState({ thread_id: id, data: {} });

        request(`/agent-helper-threads/thread/${id}`).then(response => {
            const { status, data } = response.data;

            if (status === 'error') {
                onClose();
                return;
            }

            this.setState({ data });

            this.thread_interval = setInterval(() => {
                this.runThread();
            }, 30000);
        });
    }

    runThread() {
        const { onClose } = this.props;
        const { thread_id } = this.state;

        request(`/agent-helper-threads/thread/${thread_id}`).then(response => {
            const { status, data } = response.data;

            if (status === 'error') {
                onClose();
                return;
            }

            this.setState({ data });
        });
    }

    createMessage() {
        const { message, thread_id } = this.state;

        this.setState({ createLoading: true });

        request(`/agent-helper-threads/createMessage/${toNumber(thread_id) > 0 ? thread_id : ''}`, { message }, 'POST').then(response => {
            const { status, data } = response.data;

            if (status === 'error') {
                this.setState({ createLoading: false });
                this.showSnackbar('error', __('Správu sa nepodarilo odoslať, kontaktujte nás prosím emailom'));
                return;
            }

            this.setState({ createLoading: false, message: '' });
            this.showThread(data.thread_id);
        });
    }

    onChangeMessage(message) {
        this.setState({ message });
    }

    isAnswerProcessing(status) {
        return _.includes(['init', 'waiting', 'processing', 'delay'], status);
    }

    renderThread() {
        const { data } = this.state;

        const email = `info@${getDomain(MARKET)}`;

        return (
            <div className="chat-window-content__data">
                <Message type="info">
                    <div>
                        <span>{__('Náš AI asistent vám vie pomôcť s otázkami ohľadne prepojení, nastavení a ostatných funkcionalít. Asistent zvyčajne odpovedá do jednej minúty, všetky konverzácie a odpovede nájdete na tomto mieste. Ak nás chcete kontaktovať napriamo, tak nám napíšte na email:')}</span>
                        <a href={`mailto:${email}`}>{email}</a>
                    </div>
                </Message>
                <div className="chat-window-content__data__thread">
                    <div className="chat-window-content__data__thread__header">
                        <div className="chat-window-content__data__thread__header__subject">{data.subject}</div>
                        <Button
                            onClick={() => this.fetch()}
                        >{__('Naspäť')}</Button>
                    </div>
                    <div className="chat-window-content__data__thread__content">
                        {_.map(data.messages, message => {
                            let answer = '';

                            if (message.status === 'processed' || message.status === 'error') {
                                answer = message.status === 'error' ? 'error' : message.answer;
                            }

                            const is_answer_processing = this.isAnswerProcessing(message.status);

                            return (
                                <>
                                    <div className="chat-window-content__data__thread__content__message message-user" key={message.id}>
                                        <div className="chat-window-content__data__thread__content__message__main">
                                            <div className="chat-window-content__data__thread__content__message__main__content">{message.message}</div>
                                        </div>
                                        <div className="chat-window-content__data__thread__content__message__time">{formatDate(message.created, 'hh:ii', false)}</div>
                                    </div>
                                    {answer !== '' && !is_answer_processing ? <div className="chat-window-content__data__thread__content__message message-assistant" key={`${message.id}-answer`}>
                                        <div className="chat-window-content__data__thread__content__message__main">
                                            <div className="chat-window-content__data__thread__content__message__main__content" dangerouslySetInnerHTML={{ __html: purify(answer) }} />
                                        </div>
                                        <div className="chat-window-content__data__thread__content__message__time">{formatDate(message.modified, 'hh:ii', false)}</div>
                                    </div> : null}
                                    {is_answer_processing ? <div className="chat-window-content__data__thread__content__message message-assistant" key={`${message.id}-answer`}>
                                        <div className="chat-window-content__data__thread__content__message__main">
                                            <div className="chat-window-content__data__thread__content__message__main__loading">
                                                {this.renderLoading(14)}
                                                <span>{__('Odpoveď sa spracováva')}</span>
                                            </div>
                                        </div>
                                    </div> : null}
                                </>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    }

    renderThreads() {
        const { data } = this.state;

        const email = `info@${getDomain(MARKET)}`;

        return (
            <div className="chat-window-content__data">
                <Message type="info">
                    <div>
                        <span>{__('Náš AI asistent vám vie pomôcť s otázkami ohľadne prepojení, nastavení a ostatných funkcionalít. Asistent zvyčajne odpovedá do jednej minúty, všetky konverzácie a odpovede nájdete na tomto mieste. Ak nás chcete kontaktovať napriamo, tak nám napíšte na email:')}</span>
                        <a href={`mailto:${email}`}>{email}</a>
                    </div>
                </Message>
                <div className="chat-window-content__data__items">
                    {_.map(data.items, item => {
                        return (
                            <ButtonBase
                                onClick={() => this.showThread(item.id)}
                                className={`chat-window-content__data__items__item ${toNumber(item.newly) === 1 ? 'message-newly' : ''}`}
                                key={item.id}
                            >
                                <div className={`chat-window-content__data__items__item__photo ${item.last_message_type === 'assistant' ? 'photo-assistant' : ''}`}>
                                    {item.last_message_type === 'assistant' ? <FaceRetouchingNaturalIcon /> : <PersonIcon />}
                                </div>
                                <div className="chat-window-content__data__items__item__info">
                                    <div className="chat-window-content__data__items__item__info__subject">{_.truncate(item.subject, { length: 48 })}</div>
                                    <div className="chat-window-content__data__items__item__info__last-message">{_.truncate(item.last_message, { length: 48 })}</div>
                                </div>
                                <div className="chat-window-content__data__items__item__time">{formatDate(item.last_message_created, 'dd.mm hh:ii')}</div>
                            </ButtonBase>
                        );
                    })}
                </div>
            </div>
        );
    }

    render() {
        const { data, message, thread_id, createLoading } = this.state;

        if (_.isEmpty(data)) {
            return <div
                className="chat-window-content chat-window-loading"
            >{this.renderLoading()}</div>;
        }

        const has_empty_message = isEmptyString(message);
        let has_processing_answer = false;

        if (_.has(data, 'messages')) {
            _.each(data.messages, message => {
                if (this.isAnswerProcessing(message.status)) {
                    has_processing_answer = true;
                }
            });
        }

        return (
            <div className="chat-window-content">
                {thread_id > 0 ? this.renderThread() : this.renderThreads()}
                <div className="chat-window-content__input">
                    <Input
                        value={message}
                        onChange={value => this.onChangeMessage(value)}
                        onKeyPress={value => { if (value === 'Enter') { this.createMessage(); } }}
                        placeholder={__('Napíšte správu...')}
                    />
                    <IconButton
                        onClick={!createLoading && !has_empty_message && !has_processing_answer ? () => this.createMessage() : () => {}}
                        className={has_empty_message || has_processing_answer ? 'chat-window-content__input__button-disabled' : ''}
                    >
                        {createLoading ? this.renderLoading(20, 'inherit') : <SendIcon />}
                    </IconButton>
                </div>
                {this.renderSnackbar()}
            </div>
        );
    }
}

export { ChatWindow };
